import { AccordionStatusVariant } from "@jsq/ds/core";
import { type ScreeningMatchType } from "~/graphql/supergraphTypes";
import {
  AKAsEntity,
  ParsedAKA,
  ScoringEntry,
  ScreeningAddress,
  ScreeningMatchUpdatedResults,
  ScreeningRecordEntityDetailsAdditionalInfo,
} from "../../types";

// Risk score thresholds enum
enum RiskScoreThresholds {
  HIGH_RISK = 80,
  MEDIUM_RISK = 40,
}

// Risk level determination function
export const getRiskLevel = (
  score: number,
): { variant: "success" | "warning" | "danger"; text: string } => {
  // Score is already on 0-100 scale
  if (score >= RiskScoreThresholds.HIGH_RISK) {
    return { variant: "danger", text: "High Risk" };
  } else if (score >= RiskScoreThresholds.MEDIUM_RISK) {
    return { variant: "warning", text: "Medium Risk" };
  } else {
    return { variant: "success", text: "Low Risk" };
  }
};

export const getProfileInfo = (
  profileInfoArray: ScreeningRecordEntityDetailsAdditionalInfo[] | undefined | null,
  key: keyof ScreeningRecordEntityDetailsAdditionalInfo,
  value: string,
) => {
  return profileInfoArray?.find((profileInfo) => profileInfo[key] === value);
};

export const parseAddressFromLexisNexisMatch = (
  Addresses: ScreeningAddress[] | null | undefined,
  inputAddressType?: "ScreeningInput" | "Matches",
) => {
  if (!Addresses) {
    return inputAddressType === "ScreeningInput" ? "Incomplete" : "--";
  }
  const line1 = `${Addresses[0]?.Street1 || ""} ${Addresses[0].Street2 || ""}`.trim();
  const line2 = `${Addresses[0]?.City || ""}`.trim();
  const line3 = `${Addresses[0]?.Country || ""}`.trim();
  // Check line1, line2 and line3 variables and remove empty lines from the final result string
  return [line1, line2, line3]
    .filter((line) => line !== "" && line.trim() !== "")
    .map((line) => line.trim())
    .join("\n");
};

type ParsedAddress = {
  label: string;
  value: string;
};

export const parseAllAddressesFromLexisNexisMatch = (
  Addresses: ScreeningAddress[] | null | undefined,
): ParsedAddress[] => {
  if (!Addresses || Addresses.length === 0) {
    return [{ label: "Address", value: "--" }];
  }

  return Addresses.map((address, index) => ({
    label: index === 0 ? "Address" : `Address ${index + 1}`,
    value: parseAddressFromLexisNexisMatch([address]),
  }));
};

export const getProfileScreeningResult = (
  key: string,
): { text: string; variant: AccordionStatusVariant } | undefined => {
  switch (key) {
    case "Unknown":
      return { text: "Potential match", variant: "warning" };
    case "GatheringInformation":
      return { text: "Gathering Information", variant: "warning" };
    case "FalsePositive":
      return { text: "False positive", variant: "success" };
    case "TrueMatch":
      return { text: "True match", variant: "error" };
    case "NoResult":
      return { text: "", variant: "success" };
    default:
      return { text: "", variant: "success" };
  }
};

export const extractName = (match: ScreeningMatchType): string => {
  const details = match?.details as {
    EntityDetails?: {
      Name?: {
        Full?: string;
      };
    };
  };
  return details?.EntityDetails?.Name?.Full || "--";
};

export const mapPepCategoryToNumber = {
  NA: undefined,
  pepCategory1: 1,
  pepCategory2: 2,
  pepCategory3: 3,
};

export const mapNumberToPepCategory = {
  1: "pepCategory1",
  2: "pepCategory2",
  3: "pepCategory3",
};

export const extractAKAs = (akaS: AKAsEntity[] | undefined): ParsedAKA[] => {
  if (!Array.isArray(akaS) || !akaS?.length) {
    return [{ text: "--", category: "" }];
  }
  return akaS.map?.((aka: AKAsEntity) => {
    return { text: aka.Name.Full, category: aka.Category };
  });
};

export const findMatchById = (matchId: number, matchResults: ScreeningMatchUpdatedResults[]) =>
  matchResults.find((matchResult) => matchResult.id === matchId);

/**
 * Sorts matches by AI score and other criteria:
 * 1. All immutable=false items first, sorted by AI score (highest to lowest)
 * 2. Then immutable=true items without modifiedAt
 * 3. Finally immutable=true items with modifiedAt (newest to oldest)
 */
export const sortScreeningMatches = (
  matches: ScreeningMatchType[],
  aiScoringResults: Map<number, ScoringEntry>,
): ScreeningMatchType[] => {
  return matches.toSorted((a, b) => {
    // Get AI scores for both items
    const scoreA = aiScoringResults.get(a.id)?.ai_score ?? 0;
    const scoreB = aiScoringResults.get(b.id)?.ai_score ?? 0;

    // First group: immutable=false items sorted by AI score
    if (!a.immutable && !b.immutable) {
      return scoreB - scoreA; // Sort by AI score (highest to lowest)
    }
    if (!a.immutable) return -1; // a should come first
    if (!b.immutable) return 1; // b should come first

    // At this point, both items are immutable=true
    // Second group: items without modifiedAt
    if (!a.modifiedAt && !b.modifiedAt) {
      return scoreB - scoreA; // Sort by AI score if both don't have modifiedAt
    }
    if (!a.modifiedAt) return -1; // Items without modifiedAt come before those with modifiedAt
    if (!b.modifiedAt) return 1;

    // Third group: items with modifiedAt, sorted by date
    const dateA = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
    const dateB = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
    return dateB - dateA;
  });
};

const RESULT_TYPE_SORT_ORDER: Record<string, number> = {
  TrueMatch: 1,
  FalsePositive: 2,
  GatheringInformation: 3,
  Unknown: 4,
  Deleted: 5,
};

export const sortTimelineMatchesByResult = (matches: ScreeningMatchType[]) => {
  return matches.toSorted((a, b) => {
    const priorityA = a.deletedAt
      ? RESULT_TYPE_SORT_ORDER["Deleted"]
      : RESULT_TYPE_SORT_ORDER[a.result];
    const priorityB = b.deletedAt
      ? RESULT_TYPE_SORT_ORDER["Deleted"]
      : RESULT_TYPE_SORT_ORDER[b.result];

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.id - b.id;
  });
};

export const sortGroupedMatches = (groupedMatches: Map<number, ScreeningMatchType[]>) =>
  Array.from(groupedMatches.entries()).sort(([keyA], [keyB]) => {
    // -1 always comes first
    if (keyA === -1) return -1;
    if (keyB === -1) return 1;

    return keyB - keyA;
  });

export const getScreeningResult = (matches: ScreeningMatchType[]): string => {
  const matchResults = matches.length > 0 ? matches.map((match) => match.result) : [];

  if (matchResults.length === 0) {
    return "No Matches Found";
  } else if (matchResults.includes("TrueMatch")) {
    return "True Match";
  } else if (matchResults.every((result) => result === "FalsePositive")) {
    return "False Positive";
  } else {
    return "Potential Matches";
  }
};

export const extractAdditionalInfoUrls = (
  additionalInfo: ScreeningRecordEntityDetailsAdditionalInfo[] | null | undefined,
): string[] => {
  if (!additionalInfo || !Array.isArray(additionalInfo)) {
    return [];
  }

  const urls: string[] = [];
  const urlRegex = /https?:\/\/[^\s|]+/g;

  for (const info of additionalInfo) {
    if (info.Type === "Other" && info.Comments) {
      const matches = info.Comments.match(urlRegex);
      if (matches) {
        urls.push(...matches.map((url) => url.trim()));
      }
    }
  }

  return urls;
};
